import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Building2,
  CalendarClock,
  Check,
  ChevronDown,
  ClipboardCheck,
  Clock,
  CloudOff,
  FileSpreadsheet,
  Fingerprint,
  LayoutDashboard,
  MapPin,
  Menu,
  Monitor,
  Plane,
  ShieldCheck,
  Smartphone,
  Tablet,
  Users,
  X,
} from 'lucide-react';
import { LegalLinks } from './Legal';
import { Logo } from '../components/logo';

/**
 * Página pública de presentación de WulfTime.
 * Los precios salen de /api/v1/public/info (configurados en Plataforma → Planes); si no hay precio, se invita a contactar.
 * Ojo con las promesas: WulfTime ayuda a cumplir la obligación de registro, nunca garantiza el cumplimiento legal.
 */

export interface PublicPlan {
  code: string;
  name: string;
  description: string;
  features: string[];
  maxEmployees: number | null;
  featured?: boolean;
  pricing: null | { currency: string; baseMonthlyCents: number; perEmployeeMonthlyCents: number; includedEmployees: number; taxIncluded?: boolean };
}

interface PublicInfo {
  trialDays: number;
  annualDiscountPct: number;
  automaticPayment: boolean;
  signupEnabled: boolean;
  contactEmail: string;
  taxRate: number;
  plans: PublicPlan[];
}

const eur = (c: number, cur = 'EUR') => new Intl.NumberFormat('es-ES', { style: 'currency', currency: cur, minimumFractionDigits: c % 100 ? 2 : 0 }).format(c / 100);

const FEATURES: { icon: typeof Clock; title: string; text: string }[] = [
  { icon: Smartphone, title: 'Fichar en un toque', text: 'Entrada, pausas y salida desde el móvil, el ordenador o una tablet en la entrada. Siempre con la hora del servidor.' },
  { icon: CalendarClock, title: 'Horarios a tu medida', text: 'Pon a mano la entrada, las pausas y la salida de cada trabajador, jornadas partidas, turnos de noche o rotativos.' },
  { icon: BarChart3, title: 'Previsto, real y computable', text: 'Ves lo planificado, lo fichado y lo que cuenta según tus reglas, con la explicación de cada minuto.' },
  { icon: ClipboardCheck, title: 'Incidencias y correcciones', text: 'Olvidos, retrasos o salidas anticipadas se detectan solos. Las correcciones nunca borran el dato original.' },
  { icon: MapPin, title: 'GPS solo al fichar', text: 'Si lo activas, la ubicación se toma únicamente en el momento del fichaje. Nunca hay seguimiento continuo.' },
  { icon: CloudOff, title: 'Funciona sin conexión', text: 'Sin cobertura se ficha igual: el fichaje se guarda y se envía al volver la conexión, marcado para revisión.' },
  { icon: FileSpreadsheet, title: 'Informes con tu logo', text: 'Resumen mensual de horas en PDF y Excel, listo para firmar, para la gestoría o para la Inspección de Trabajo.' },
  { icon: Plane, title: 'Vacaciones y ausencias', text: 'Solicitudes, aprobaciones y saldos de días. El calendario laboral y los festivos se tienen en cuenta solos.' },
  { icon: ShieldCheck, title: 'Seguro y auditable', text: 'Verificación en dos pasos, datos cifrados, permisos por centro y un registro de auditoría a prueba de cambios.' },
];

const SECTORS = ['Hostelería y hoteles', 'Limpieza y servicios', 'Comercio y retail', 'Oficinas y despachos', 'Clínicas y centros', 'Logística y almacenes'];

const FAQ: [string, ReactNode][] = [
  ['¿Es obligatorio llevar el registro de jornada?', 'Sí. En España las empresas deben registrar a diario el inicio y el fin de la jornada de cada trabajador y conservarlo cuatro años (art. 34.9 del Estatuto de los Trabajadores). WulfTime te ayuda a llevarlo de forma ordenada y verificable; la responsabilidad de cumplir sigue siendo de la empresa.'],
  ['¿Tengo que instalar algo?', 'No. Funciona en el navegador del ordenador y del móvil. Para quien prefiera, hay app para Android y un modo kiosco para una tablet en la entrada.'],
  ['¿Qué pasa si un trabajador se olvida de fichar?', 'Recibe un aviso. Si aun así no ficha la salida, puedes hacer que se registre automáticamente a la hora prevista (marcada como automática) y el trabajador puede pedir una corrección.'],
  ['¿Se controla dónde están mis trabajadores?', 'No. Si activas el GPS, solo se consulta la ubicación en el momento de fichar y se informa al trabajador. No hay seguimiento continuo.'],
  ['¿Puedo sacar los informes para la Inspección o la gestoría?', 'Sí: informes por trabajador y periodo en PDF, Excel o CSV, con tu logo, el detalle diario, las incidencias y las correcciones.'],
  ['¿Qué pasa con mis datos si dejo WulfTime?', 'Son tuyos: puedes exportarlos en cualquier momento en formatos abiertos.'],
];

export function LandingPage() {
  const [menu, setMenu] = useState(false);
  const { data: info } = useQuery({ queryKey: ['public-info'], queryFn: () => fetch('/api/v1/public/info').then((r) => r.json() as Promise<PublicInfo>), staleTime: 30_000 });

  useEffect(() => {
    document.title = 'WulfTime · Registro horario para empresas';
  }, []);

  const trial = info?.trialDays ?? 7;
  const [annual, setAnnual] = useState(false);
  const cta = info?.signupEnabled === false ? { to: `mailto:${info.contactEmail}`, label: 'Contactar' } : { to: '/signup', label: `Prueba gratis ${trial} días` };

  const nav = [
    ['#funciones', 'Funciones'],
    ['#como-funciona', 'Cómo funciona'],
    ['#precios', 'Precios'],
    ['#preguntas', 'Preguntas'],
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* Cabecera */}
      <header className="sticky top-0 z-30 border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <a href="#inicio" aria-label="WulfTime, inicio">
            <Logo />
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex" aria-label="Secciones">
            {nav.map(([href, label]) => (
              <a key={href} href={href} className="hover:text-brand-800">
                {label}
              </a>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <Link to="/login" className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
              Entrar
            </Link>
            <CtaLink to={cta.to} className="px-4 py-2 text-sm">
              {cta.label}
            </CtaLink>
          </div>
          <button className="rounded-lg p-2 md:hidden" aria-label={menu ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
            {menu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menu && (
          <div className="border-t border-slate-100 bg-white px-4 py-3 md:hidden">
            {nav.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenu(false)} className="block rounded-lg px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {label}
              </a>
            ))}
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Link to="/login" className="rounded-lg border border-slate-200 px-3 py-2 text-center text-sm font-medium">
                Entrar
              </Link>
              <CtaLink to={cta.to} className="px-3 py-2 text-center text-sm">
                Probar gratis
              </CtaLink>
            </div>
          </div>
        )}
      </header>

      {/* Portada */}
      <section id="inicio" className="relative overflow-hidden bg-gradient-to-b from-brand-50 via-white to-white">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-16 pt-12 md:grid-cols-2 md:pb-24 md:pt-20">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold text-brand-800">
              <Fingerprint className="h-3.5 w-3.5" /> Registro horario para empresas en España
            </p>
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-brand-950 sm:text-5xl">
              El control horario de tu equipo, <span className="text-brand-600">sin papeles ni líos</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-slate-600">
              Tus trabajadores fichan desde el móvil, el ordenador o una tablet. Tú ves en tiempo real quién está trabajando, las horas de cada uno y los informes listos para la gestoría o la Inspección.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to={cta.to} className="px-6 py-3 text-base">
                {cta.label}
              </CtaLink>
              <a href="#como-funciona" className="rounded-xl border border-slate-300 bg-white px-6 py-3 text-base font-semibold text-slate-800 hover:border-brand-300 hover:text-brand-800">
                Ver cómo funciona
              </a>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
              {[`${trial} días gratis`, 'Sin permanencia', 'Soporte en español'].map((t) => (
                <li key={t} className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-600" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <HeroMockup />
        </div>
      </section>

      {/* Canales */}
      <section className="border-y border-slate-100 bg-slate-50">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-8 text-center sm:grid-cols-4">
          {[
            [Smartphone, 'App Android', 'y web en el móvil'],
            [Monitor, 'Web', 'desde cualquier ordenador'],
            [Tablet, 'Kiosco', 'tablet en la entrada con PIN o QR'],
            [LayoutDashboard, 'Panel de empresa', 'en tiempo real'],
          ].map(([Icon, t, s]) => {
            const I = Icon as typeof Clock;
            return (
              <div key={t as string} className="flex flex-col items-center gap-1">
                <I className="h-7 w-7 text-brand-600" />
                <p className="font-semibold text-brand-950">{t as string}</p>
                <p className="text-xs text-slate-500">{s as string}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Funciones */}
      <section id="funciones" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-20">
        <SectionTitle kicker="Funciones" title="Todo lo que necesitas para el registro de jornada" text="Pensado para el día a día de empresas con personal en sala, en ruta, en turnos o en oficina." />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200 p-6 transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lg hover:shadow-brand-900/5">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-brand-950">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cómo funciona */}
      <section id="como-funciona" className="scroll-mt-20 bg-brand-950 text-white">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <SectionTitle dark kicker="Cómo funciona" title="En marcha en una mañana" text="Sin instalaciones ni técnicos. Si te atascas, te ayudamos." />
          <ol className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              ['Da de alta tu empresa', 'Crea la cuenta, añade tus centros y sube tu logo. Puedes importar la plantilla desde Excel.'],
              ['Pon los horarios', 'Entrada, pausas y salida de cada trabajador, o plantillas y turnos para todo un equipo.'],
              ['Invita a tu equipo', 'Cada trabajador recibe un enlace para crear su acceso y empieza a fichar desde el móvil o el kiosco.'],
            ].map(([t, s], i) => (
              <li key={t} className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-bold text-brand-950">{i + 1}</span>
                <h3 className="mt-4 text-lg font-semibold">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-200">{s}</p>
              </li>
            ))}
          </ol>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-2">
            <span className="mr-2 text-sm text-brand-300">Ideal para:</span>
            {SECTORS.map((s) => (
              <span key={s} className="rounded-full bg-white/10 px-3 py-1 text-sm">
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Precios */}
      <section id="precios" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-20">
        <SectionTitle kicker="Precios" title="Pagas solo por los trabajadores activos" text={`Mensual con renovación automática o anual con descuento. Prueba gratis ${trial} días con todas las funciones.`} />
        <div className="mt-8 flex justify-center">
          <div role="radiogroup" aria-label="Periodicidad" className="inline-flex rounded-xl bg-slate-100 p-1 text-sm font-medium">
            <button type="button" role="radio" aria-checked={!annual} onClick={() => setAnnual(false)} className={`rounded-lg px-4 py-2 ${!annual ? 'bg-white shadow-sm text-brand-950' : 'text-slate-600'}`}>
              Mensual
            </button>
            <button type="button" role="radio" aria-checked={annual} onClick={() => setAnnual(true)} className={`rounded-lg px-4 py-2 ${annual ? 'bg-white shadow-sm text-brand-950' : 'text-slate-600'}`}>
              Anual {info?.annualDiscountPct ? <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700">−{info.annualDiscountPct}%</span> : null}
            </button>
          </div>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {(info?.plans ?? []).map((p, i) => (
            <PlanCard key={p.code} plan={p} featured={info!.plans.some((x) => x.featured) ? Boolean(p.featured) : i === 1} cta={cta} taxRate={info?.taxRate ?? 21} annualDiscountPct={annual ? (info?.annualDiscountPct ?? 0) : null} />
          ))}
          {!info && [0, 1, 2].map((i) => <div key={i} className="h-96 animate-pulse rounded-2xl bg-slate-100" />)}
        </div>
        <p className="mt-6 text-center text-sm text-slate-500">Cobro automático con tarjeta o domiciliación SEPA; el plan anual también por transferencia. Sin permanencia: cancela cuando quieras. Un impago nunca bloquea el registro de jornada de tus trabajadores.</p>
      </section>

      {/* Preguntas */}
      <section id="preguntas" className="scroll-mt-20 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 py-20">
          <SectionTitle kicker="Preguntas frecuentes" title="Resolvemos tus dudas" />
          <div className="mt-10 space-y-3">
            {FAQ.map(([q, a]) => (
              <details key={q} className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-sm">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-brand-950">
                  {q}
                  <ChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Llamada final */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-950 to-brand-700 px-6 py-14 text-center text-white sm:px-12">
          <img src="/brand/wulftime-mark.svg" alt="" className="pointer-events-none absolute -right-10 -top-6 h-64 opacity-10" />
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Empieza hoy a fichar con WulfTime</h2>
          <p className="mx-auto mt-4 max-w-xl text-brand-200">Crea tu empresa en minutos y prueba todas las funciones durante {trial} días. Hoy no se cobra nada y puedes cancelar durante la prueba.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={cta.to.startsWith('mailto:') ? '/login' : cta.to} className="rounded-xl bg-white px-6 py-3 font-semibold text-brand-950 hover:bg-brand-50">
              {cta.label}
            </Link>
            {info?.contactEmail && (
              <a href={`mailto:${info.contactEmail}`} className="rounded-xl px-6 py-3 font-semibold text-white ring-1 ring-white/40 hover:bg-white/10">
                Hablar con nosotros
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Pie */}
      <footer className="border-t border-slate-100">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 text-sm text-slate-500 sm:flex-row">
          <Logo size="sm" />
          <p className="text-center">© {new Date().getFullYear()} WulfTime · Registro horario y gestión de jornadas{info?.contactEmail ? ` · ${info.contactEmail}` : ''}</p>
          <Link to="/login" className="font-medium text-brand-700 hover:underline">
            Acceso clientes
          </Link>
        </div>
        <LegalLinks className="mx-auto max-w-6xl px-4 pb-8 text-xs text-slate-500" />
      </footer>
    </div>
  );
}

function CtaLink({ to, className = '', children }: { to: string; className?: string; children: ReactNode }) {
  const cls = `rounded-xl bg-brand-700 font-semibold text-white shadow-sm shadow-brand-900/20 transition hover:bg-brand-800 ${className}`;
  return to.startsWith('mailto:') ? (
    <a href={to} className={cls}>
      {children}
    </a>
  ) : (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
}

function SectionTitle({ kicker, title, text, dark = false }: { kicker: string; title: string; text?: string; dark?: boolean }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className={`text-sm font-semibold uppercase tracking-wider ${dark ? 'text-brand-300' : 'text-brand-600'}`}>{kicker}</p>
      <h2 className={`mt-2 text-3xl font-bold tracking-tight sm:text-4xl ${dark ? 'text-white' : 'text-brand-950'}`}>{title}</h2>
      {text && <p className={`mt-4 text-lg ${dark ? 'text-brand-200' : 'text-slate-600'}`}>{text}</p>}
    </div>
  );
}

/** Lo que paga al mes una empresa con `n` trabajadores (sin IVA). */
export function monthlyFor(p: NonNullable<PublicPlan['pricing']>, n: number) {
  return p.baseMonthlyCents + Math.max(0, n - p.includedEmployees) * p.perEmployeeMonthlyCents;
}

export function PlanCard({ plan, featured, cta, taxRate, annualDiscountPct }: { plan: PublicPlan; featured: boolean; cta: { to: string; label: string }; taxRate: number; annualDiscountPct: number | null }) {
  const p0 = plan.pricing;
  // En anual se muestra el precio mensual equivalente con el descuento aplicado
  const k = annualDiscountPct === null ? 1 : 1 - annualDiscountPct / 100;
  const p = p0 && { ...p0, baseMonthlyCents: Math.round(p0.baseMonthlyCents * k), perEmployeeMonthlyCents: Math.round(p0.perEmployeeMonthlyCents * k) };
  const hasPrice = p && (p.baseMonthlyCents > 0 || p.perEmployeeMonthlyCents > 0);
  // Ejemplo realista: 10 trabajadores (o el máximo del plan, si es menor)
  const exampleN = plan.maxEmployees ? Math.min(10, plan.maxEmployees) : 10;
  return (
    <div className={`relative flex flex-col rounded-2xl border bg-white p-7 ${featured ? 'border-brand-600 shadow-xl shadow-brand-900/10' : 'border-slate-200'}`}>
      {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-700 px-3 py-1 text-xs font-semibold text-white">El más elegido</span>}
      <h3 className="text-lg font-semibold text-brand-950">{plan.name.replace(/^WulfTime /, '')}</h3>
      <p className="mt-1 text-sm text-slate-500">{plan.description}</p>
      <div className="mt-5 min-h-[92px]">
        {hasPrice ? (
          p!.baseMonthlyCents > 0 && p!.includedEmployees > 0 ? (
            <>
              <p className="text-4xl font-bold text-brand-950">
                {eur(p!.baseMonthlyCents, p!.currency)}
                <span className="text-base font-medium text-slate-500"> / mes</span>
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Incluye {p!.includedEmployees} trabajadores{p!.perEmployeeMonthlyCents > 0 ? ` · ${eur(p!.perEmployeeMonthlyCents, p!.currency)} por trabajador adicional` : ''}
              </p>
            </>
          ) : (
            <>
              <p className="text-4xl font-bold text-brand-950">
                {eur(p!.perEmployeeMonthlyCents, p!.currency)}
                <span className="text-base font-medium text-slate-500"> / trabajador · mes</span>
              </p>
              {p!.baseMonthlyCents > 0 && <p className="mt-1 text-sm text-slate-600">+ {eur(p!.baseMonthlyCents, p!.currency)}/mes de cuota fija</p>}
            </>
          )
        ) : (
          <p className="text-2xl font-bold text-brand-950">Consúltanos</p>
        )}
        {hasPrice && (
          <p className="mt-1 text-xs text-slate-500">
            {p0!.taxIncluded ? 'IVA incluido' : `Sin IVA (${taxRate}%)`}{annualDiscountPct !== null ? ' · pagando el año' : ''} · Con {exampleN} trabajadores: <strong>{eur(monthlyFor(p!, exampleN), p!.currency)}/mes</strong>
          </p>
        )}
      </div>
      <ul className="mt-6 flex-1 space-y-2.5 text-sm">
        <li className="flex gap-2">
          <Users className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" /> {plan.maxEmployees ? `Hasta ${plan.maxEmployees} trabajadores` : 'Trabajadores ilimitados'}
        </li>
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {f}
          </li>
        ))}
      </ul>
      <CtaLink to={cta.to} className={`mt-7 py-2.5 text-center text-sm ${featured ? '' : '!bg-brand-50 !text-brand-800 hover:!bg-brand-100'}`}>
        {cta.label}
      </CtaLink>
    </div>
  );
}

/** Maqueta ilustrativa (HTML, sin imágenes): el móvil del trabajador y el resumen del panel. */
function HeroMockup() {
  return (
    <div className="relative mx-auto w-full max-w-md" aria-hidden>
      <div className="absolute -left-6 top-10 h-40 w-40 rounded-full bg-brand-200/50 blur-3xl" />
      <div className="absolute -right-4 bottom-0 h-48 w-48 rounded-full bg-brand-300/40 blur-3xl" />
      {/* Panel */}
      <div className="relative ml-auto w-[88%] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl shadow-brand-900/10">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
            <Building2 className="h-3.5 w-3.5" /> Hoy en tu empresa
          </p>
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">En directo</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          {[
            ['18', 'Trabajando', 'text-emerald-700'],
            ['3', 'En pausa', 'text-amber-600'],
            ['1', 'Sin fichar', 'text-red-600'],
          ].map(([n, l, c]) => (
            <div key={l} className="rounded-xl bg-slate-50 py-2">
              <p className={`text-xl font-bold ${c}`}>{n}</p>
              <p className="text-[10px] text-slate-500">{l}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2">
          {[
            ['Lucía M.', 'Recepción', '07:58', '8h 02m', 'bg-emerald-500'],
            ['Juan G.', 'Pisos', '08:03', '7h 41m', 'bg-emerald-500'],
            ['Sofía R.', 'Cocina', '09:00', 'En pausa', 'bg-amber-500'],
          ].map(([n, d, t, h, c]) => (
            <div key={n} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-xs">
              <span className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${c}`} />
                <span className="font-medium text-slate-800">{n}</span>
                <span className="text-slate-400">{d}</span>
              </span>
              <span className="tabular-nums text-slate-600">
                {t} · {h}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Móvil */}
      <div className="relative -mt-24 w-52 rounded-[2rem] border-[6px] border-brand-950 bg-white p-3 shadow-2xl shadow-brand-900/30">
        <div className="mx-auto mb-2 h-1.5 w-14 rounded-full bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <img src="/brand/wulftime-mark.svg" alt="" className="h-5 w-5" />
          <span className="text-[11px] font-semibold text-brand-950">WulfTime</span>
        </div>
        <p className="mt-3 text-[10px] text-slate-500">Lunes · Hoteles Demo</p>
        <p className="text-2xl font-bold tabular-nums text-brand-950">08:00</p>
        <p className="text-[10px] text-slate-500">Jornada 08:00 – 16:00</p>
        <div className="mt-3 rounded-xl bg-emerald-600 py-3 text-center text-sm font-semibold text-white">Fichar entrada</div>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <div className="rounded-lg bg-slate-100 py-2 text-center text-[10px] font-medium text-slate-600">Pausa</div>
          <div className="rounded-lg bg-slate-100 py-2 text-center text-[10px] font-medium text-slate-600">Salida</div>
        </div>
        <p className="mt-2 flex items-center justify-center gap-1 text-[9px] text-slate-400">
          <MapPin className="h-2.5 w-2.5" /> Ubicación solo al fichar
        </p>
      </div>
    </div>
  );
}
